import { Control, FieldValues, useController, Path } from "react-hook-form";
import styled from "styled-components";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { Input, Label, Larger } from "@/components/Forms";
import ValidationMessage from "@/components/Forms/ValidationMessage";
import { api } from "@/utils/api";
import useDebounce from "@/hooks/useDebounce";
import Text from "@/components/Text";

const Explanation = styled.span`
  color: ${({ theme }) => theme.colors.gray};
`;

const Box = styled.div`
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
  border-radius: 4px;
  padding: 4px;
  position: absolute;
  top: calc(100% + 8px);
  width: 100%;
  z-index: 2;
  max-height: 220px;
  overflow-y: auto;
`;

const BoxButton = styled.button.attrs({ type: "button" })`
  display: flex;
  width: 100%;
  height: 40px;
  align-items: center;
  padding: 0 12px;
  &:hover {
    background: ${({ theme }) => theme.colors.grayscaleLight};
  }
`;

const Wrap = styled.div`
  position: relative;
`;

interface Props<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
}

const AuthorField = <T extends FieldValues>({ control, name }: Props<T>) => {
  const [showSuggestions, setShowSuggestions] = useState(false);

  const { data: names } = useQuery(
    ["users", { onlyNames: true }],
    () => api.get<never, string[]>("/v1/users"),
    {
      staleTime: 180 * 1000,
    }
  );

  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController({
    name,
    control,
  });

  const debouncedValue = useDebounce(value, 350);

  const suggestions = names?.filter((n) =>
    n.toLowerCase().includes(debouncedValue.toLowerCase())
  );

  return (
    <Larger>
      <Wrap>
        <Label htmlFor="authorInput">
          Author <Explanation>(Editor or Thumbnailer name)</Explanation>
        </Label>
        <Input
          onChange={onChange}
          value={value}
          type="text"
          id="authorInput"
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
        />
        {!!suggestions?.length && showSuggestions && !!value.length && (
          <Box>
            {suggestions.map((name, index) => (
              <BoxButton
                key={index}
                onClick={() => {
                  onChange(name);
                  setShowSuggestions(false);
                }}
              >
                <Text as="span" $type="captionSemibold">
                  {name}
                </Text>
              </BoxButton>
            ))}
          </Box>
        )}
      </Wrap>
      {!!error && <ValidationMessage>{error.message}</ValidationMessage>}
    </Larger>
  );
};

export default AuthorField;
